import { CustomFieldResourceType } from "@repo/app-types/entities/customField";
import {
  codeToolMaxTimeoutMs,
  defaultCodeToolTimeoutMs,
  type CodeToolSecretListing,
} from "@repo/app-types/entities/tools/code";
import { buildCodeToolScriptApi } from "@repo/app-types/entities/tools/codeToolScriptApi";
import type { CustomToolParameter } from "@repo/app-types/entities/tools/custom";
import { CreatableComboboxField } from "@repo/ui/combobox/creatableComboboxField";
import { Input } from "@repo/ui/Inputs/input";
import { useMemo, type FC } from "react";
import { useAllUserCustomFieldDefinitionsQuery } from "../../../api/entities/userCustomField.js";
import {
  findCodeToolSecretsOutsideDomains,
  type CodeToolFormSecret,
} from "../formConfigs/codeToolSecrets.js";
import type { CodeToolTestInputFormSettings } from "../formConfigs/codeToolTestInput.js";
import type { ToolMockFormSettings } from "../formConfigs/toolMockResponse.js";
import { applyAllowedDomainsChange } from "../formConfigs/webDocumentResearchAllowedDomains.js";
import {
  CodeToolCodeField,
  type CodeToolErrorPosition,
} from "./codeToolCodeField.js";
import { CodeToolSecretsEditor } from "./codeToolSecretsEditor.js";
import { CustomToolParameterFormBuilder } from "./customToolParameterFormBuilder.js";
import { SharedToolFormFields } from "./sharedToolFormFields.js";
import { SigningKeyDisplay } from "./signingKeyDisplay.js";
import { ToolMockResponseField } from "./toolMockResponseField.js";

interface CodeToolFormProps {
  name: string;
  description: string;
  settings: CodeToolFormSettings;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onSettingsChange: (settings: Partial<CodeToolFormSettings>) => void;
  isEditMode: boolean;
  /** Set while a save is in flight: the code editors take no edits. */
  isReadOnly: boolean;
}

/** The test input is edited in the test panel beside the form. */
export interface CodeToolFormSettings
  extends ToolMockFormSettings, CodeToolTestInputFormSettings {
  code: string;
  codeError: string | undefined;
  /** Where the saved code fails to compile, when the API says. */
  codeErrorPosition: CodeToolErrorPosition | undefined;
  parameters: readonly CustomToolParameter[];
  allowedDomains: readonly string[];
  allowedDomainsError: string | undefined;
  timeout: string;
  timeoutError: string | undefined;
  secrets: readonly CodeToolFormSecret[];
  /** The secrets as stored, to tell which ones the form has removed. */
  storedSecrets: readonly CodeToolSecretListing[];
}

export const CodeToolForm: FC<CodeToolFormProps> = (props) => {
  const {
    name,
    description,
    settings,
    onNameChange,
    onDescriptionChange,
    onSettingsChange,
    isEditMode,
    isReadOnly,
  } = props;

  const { data: customFieldDefinitions } =
    useAllUserCustomFieldDefinitionsQuery();
  // From the form's unsaved state, so a parameter or secret just added
  // completes straight away.
  const scriptApi = useMemo(
    () =>
      buildCodeToolScriptApi({
        parameters: settings.parameters.filter(({ name }) => name !== ""),
        contactCustomFields: (customFieldDefinitions ?? []).filter(
          ({ resource_type }) =>
            resource_type === CustomFieldResourceType.CONTACT,
        ),
        interactionCustomFields: (customFieldDefinitions ?? []).filter(
          ({ resource_type }) =>
            resource_type === CustomFieldResourceType.GRADING,
        ),
        secretNames: settings.secrets
          .map(({ name }) => name)
          .filter((name) => name !== ""),
      }),
    [settings.parameters, settings.secrets, customFieldDefinitions],
  );

  const secretsOutsideDomains = findCodeToolSecretsOutsideDomains(
    settings.secrets,
    settings.allowedDomains,
  );

  return (
    <>
      {isEditMode && <SigningKeyDisplay />}

      <SharedToolFormFields
        name={name}
        description={description}
        onNameChange={onNameChange}
        onDescriptionChange={onDescriptionChange}
        namePlaceholder="Look up the next delivery"
        descriptionPlaceholder="This tool finds the caller's next delivery and when it is due. The agent should use this if the caller asks about an order."
      />

      <CustomToolParameterFormBuilder
        label="Parameters"
        description="Define the parameters the agent should provide. The script reads them as input.params."
        value={settings.parameters}
        onChange={(parameters) => onSettingsChange({ parameters })}
      />

      <CodeToolCodeField
        isReadOnly={isReadOnly}
        value={settings.code}
        onChange={(code) =>
          onSettingsChange({
            code,
            codeError: undefined,
            codeErrorPosition: undefined,
          })
        }
        errorMessage={settings.codeError}
        errorPosition={settings.codeErrorPosition}
        scriptApi={scriptApi}
      />

      <div>
        <CreatableComboboxField
          label="Allowed domains"
          placeholder="Add a domain (e.g. api.example.com)"
          value={settings.allowedDomains}
          onChange={(allowedDomains) =>
            onSettingsChange(
              applyAllowedDomainsChange(
                settings.allowedDomains,
                allowedDomains,
              ),
            )
          }
          description="The script can only send requests to these domains. Enter exact hostnames without https://, a path, port, or wildcard. Subdomains must be added separately."
          errorMessage={settings.allowedDomainsError}
        />
        {secretsOutsideDomains.length > 0 ? (
          <div className="mt-2 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm text-amber-800">
            <p>
              These secrets are bound to domains the tool no longer allows, and
              won&apos;t be sent there:
            </p>
            <ul className="mt-1 list-disc pl-5">
              {secretsOutsideDomains.map((secret, index) => (
                <li key={index}>
                  <span className="font-mono">{secret.name}</span> (
                  {secret.domains.join(", ")})
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <CodeToolSecretsEditor
        secrets={settings.secrets}
        toolDomains={settings.allowedDomains}
        onChange={(secrets) => onSettingsChange({ secrets })}
      />

      <Input
        id="codeToolTimeout"
        label="Timeout (ms)"
        type="number"
        min={1}
        max={codeToolMaxTimeoutMs}
        value={settings.timeout}
        onChange={(e) =>
          onSettingsChange({ timeout: e.target.value, timeoutError: undefined })
        }
        placeholder={defaultCodeToolTimeoutMs.toString()}
        description={`How long a run may take, at most ${codeToolMaxTimeoutMs.toLocaleString()} ms.`}
        errorDescription={settings.timeoutError}
      />

      <ToolMockResponseField
        settings={settings}
        onSettingsChange={onSettingsChange}
        isReadOnly={isReadOnly}
      />
    </>
  );
};
